/**
 * Why an APPROVED shift has no pay entry (api#1148) — `pay_gap` and
 * `pay_gap_message` on GET /timesheets, /timesheets/:id and
 * /timesheets/my/period/:id.
 *
 * The API records the reason on the approval's audit row and projects it for
 * as long as the shift still has no `timesheet_entries` row; a later Generate
 * that files the pay clears it. Before this, the reason was in the approve
 * response and nowhere else: TNT's admin went two weeks unpaid on
 * `rate_not_effective_on_date` with nothing on any screen.
 *
 * ── NULL IS "NOTHING RECORDED", NOT "PAID" ─────────────────────────────────
 * `pay_gap` is null for a shift that is not approved, for one Generate has
 * filed, for field time paid through the ticket, AND for every shift approved
 * before api#1148. So the absence of a note claims nothing — no "Paid" badge
 * is ever drawn from it.
 *
 * ── ONE OF THESE IS NOT A GAP ──────────────────────────────────────────────
 * `job_pay_exempt` means somebody already decided this worker is not paid from
 * jobs. It is information, rendered in slate, never in the warning colour —
 * the same rule `isJobPayExemptGap` applies on the shop tab.
 */
import { SHOP_RATE_GAP_FALLBACK_MESSAGES, isJobPayExemptGap, isShopRateGap } from '../constants/shop-pay';

export interface PayGapNote {
  tone: 'info' | 'warning';
  title: string;
  message: string;
}

/**
 * Fallback wording for the one gap that is not a shop-rate gap. The server's
 * `pay_gap_message` wins whenever it arrives; this covers a dropped message.
 */
const OTHER_GAP_FALLBACK: Record<string, string> = {
  no_open_pay_period:
    'No open pay period covers this shift, so its pay could not be filed. Open or create the '
    + 'pay period, then run Generate.',
};

/**
 * The note for a shift, or null when there is nothing to say. The server's
 * sentence wins; a known reason with no sentence gets the local one; an
 * unknown reason still gets a sentence, never a blank.
 */
export function payGapNote(gap: unknown, message: unknown): PayGapNote | null {
  if (typeof gap !== 'string' || gap.trim() === '') return null;
  const server = typeof message === 'string' && message.trim() !== '' ? message.trim() : null;
  const exempt = isJobPayExemptGap(gap);
  const fallback = isShopRateGap(gap)
    ? SHOP_RATE_GAP_FALLBACK_MESSAGES[gap]
    : OTHER_GAP_FALLBACK[gap]
      ?? 'This shift is approved but no pay was filed for it, and the reason was not reported.';
  return {
    tone: exempt ? 'info' : 'warning',
    title: exempt ? 'Not paid from jobs' : 'No pay filed',
    message: server ?? fallback,
  };
}
